import { stackTarget } from '../inventory/move-rules';
import type { InventoryMoveReceipt } from '../../ft3d-move-handlers/inventory-move-receipt';
import { weaponEnchantmentAttackLayers, type ExplicitWeaponReaders } from './explicit-weapon-attack';
import type { RollSource } from './roll-source';
import { rowLayerNames } from '../compile/row-layer-names';
import { readLayerListEntryUuid } from '../compile/inventory-raw-readers';

type Row = Record<string, unknown>;
export interface WeaponInventoryMove {
  inventory_id: string;
  before: { possess_id: string; equipped: string; item_qty: number; inventory_deleted: number };
  after: { possess_id: string; equipped: string; x_pos: number; y_pos: number; z_pos: number; inventory_wares: number };
}
const upper = (v: unknown): string => String(v ?? '').toUpperCase();

export interface WeaponInventoryChange {
  inventory_id: string;
  before: { possess_id: string; item_qty: number; inventory_deleted: number };
  after: { item_qty: number; inventory_deleted: number };
  recovery?: { inventory_id: string; possess_id: string; created: boolean; before_qty: number; item_qty: number;
    before?: { possess_id: string; inventory_deleted: number; equipped: string; x_pos: number; y_pos: number; z_pos: number };
    x_pos: number; y_pos: number; z_pos: number; row: Row };
  log_type: 'inventory_deleted' | 'drop_inventory' | 'impale_inventory' | 'throw_inventory';
  /** Cosmetic flight choice carried with the existing operation; never a save/rules input. */
  throw_flight?: { arcing: boolean };
}
const SUSTAINABLE_AMMO = new Set(['166EBD0E-7FC0-7351-5F82-C84F9B48565D', '1F7C8699-5708-6D6A-4FD7-F69D28777AED',
  'DC50590B-2070-766A-43D5-76E5A0A8D5E5', 'AE3F2A46-7143-18E2-F8DB-42A765DF46B3']);
/** Inventory-only size effects remain inert during a shot. */
export const ATTACK_INERT_AMMO_LAYERS: ReadonlySet<string> = new Set(['weight_multiplier', 'cost_gp_multiplier']);
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const AMMO_EFFECT_LAYERS = new Set([...ATTACK_INERT_AMMO_LAYERS, 'cost_gp', 'enchantment_item_type',
  'weapon_attack_mod', 'item_composition', 'roll_type_adv']);
const AMMO_FRAGMENT_LAYERS = new Set([...AMMO_EFFECT_LAYERS, 'dice', 'die', 'mod', 'roll_type']);
const EXTRA_WEAPON_DAMAGE = '42A38751-5549-CDE7-528E-AED309A2B07D';
const DAMAGE_ROLL = '82BBB3B6-4646-48E8-A5A2-8DC0947A91D0';

function ammoEffectPlan(ammo: Row, read: ExplicitWeaponReaders): Row[] | string {
  if (read.entries(ammo, 'extra_damage_roll').length) return 'weapon:ammo-effects';
  const effects = new Map<string, Row>();
  const unavailable = (id: string): string => 'weapon:ammo-effects-unavailable:' + id;
  const inspect = (raw: unknown): string | null => {
    const entry = record(raw), nested = record(entry?.item_arr) ?? entry;
    const id = upper(readLayerListEntryUuid(nested ?? raw));
    const definition = nested && record(nested.item_layer_types) ? nested : read.resolveItem?.(id);
    if (!definition || !record(definition.item_layer_types)) return unavailable(id || 'part');
    const fragment = upper(read.value(definition, 'roll_type'));
    if (fragment && fragment !== EXTRA_WEAPON_DAMAGE) return 'weapon:ammo-effects';
    const supported = fragment ? AMMO_FRAGMENT_LAYERS : AMMO_EFFECT_LAYERS;
    if (rowLayerNames(definition).some(key => !supported.has(key))) return 'weapon:ammo-effects';
    if (read.entries(definition, 'roll_type_adv').some(entry => upper(readLayerListEntryUuid(entry)) !== DAMAGE_ROLL))
      return 'weapon:ammo-effects';
    for (const key of ['weapon_attack_mod', ...(fragment ? ['dice', 'die', 'mod'] : [])]) {
      const value = read.value(definition, key);
      if (value != null && !Number.isSafeInteger(Number(value))) return unavailable(id + ':' + key);
    }
    // Zero-dice modifiers use the existing weapon extra-fragment executor.
    // Rolled/status/trigger extras retain a refusal until their full contract is ported.
    if (fragment && (Number(read.value(definition, 'dice') ?? 0) !== 0 || Number(read.value(definition, 'die') ?? 0) !== 0))
      return 'weapon:ammo-effects';
    effects.set(id, fragment ? { ...definition, item_layer_types: { ...record(definition.item_layer_types),
      dice: Number(read.value(definition, 'dice') ?? 0), die: Number(read.value(definition, 'die') ?? 0) } } : definition);
    return null;
  };
  // The member manifest proves coverage even when the compiler omitted an unresolved grant.
  const inspectTemplate = (id: string, source: Row): string | null => {
    const metadata = record(source.enchantment_templates);
    const members = source.enchantment_template_enchantments;
    if (upper(read.itemField(metadata, 'enchantment_template_id')) !== id || !Array.isArray(members)) return unavailable(id);
    for (const member of members) {
      const row = record(member);
      if (!row) return unavailable(id);
      if (read.itemField(row, 'enchantment_type') !== 'passive_ability') return 'weapon:ammo-effects';
      const reason = inspect(read.itemField(row, 'enchantment_item_id'));
      if (reason) return reason;
    }
    return null;
  };
  const template = upper(read.inventoryField(ammo, 'enchantment_template_id'));
  if (template) {
    const reason = inspectTemplate(template, ammo);
    if (reason) return reason;
  }
  for (const applied of read.entries(ammo, 'enchantment_template_applied')) {
    const id = upper(readLayerListEntryUuid(applied));
    const nested = record(applied);
    const source = Array.isArray(nested?.enchantment_template_enchantments) ? nested : read.resolveItem?.(id);
    const reason = source ? inspectTemplate(id, source) : unavailable(id || 'template');
    if (reason) return reason;
  }
  const abilities = read.computedValue(ammo, 'enchantment', 'abilities');
  if (abilities != null) {
    if (!(Array.isArray(abilities) && !abilities.length) && !record(abilities)) return unavailable('abilities');
    for (const [bucket, entries] of Object.entries(abilities)) {
      if (bucket !== 'passive_ability') return 'weapon:ammo-effects';
      if (!Array.isArray(entries)) return unavailable('abilities');
      for (const entry of entries) { const reason = inspect(entry); if (reason) return reason; }
    }
  }
  for (const entry of read.entries(ammo, 'passive_ability')) {
    const reason = inspect(entry); if (reason) return reason;
  }
  // Never infer that a cold inventory reference is plain ammunition.
  const itemId = upper(read.inventoryField(ammo, 'inventory_item_id'));
  const definition = record(ammo.items) && record(ammo.item_layer_types) ? ammo
    : record(record(ammo.catalog)?.[itemId]) ?? read.resolveItem?.(itemId);
  if (!definition || !record(definition.item_layer_types)) return unavailable(itemId || 'ammo');
  const direct = ['weapon_attack_mod', 'item_composition', 'roll_type_adv', 'roll_type', 'dice', 'die', 'mod'];
  const layers: Row = {};
  for (const key of new Set([...rowLayerNames(definition), ...rowLayerNames(ammo)])) {
    if (direct.includes(key)) layers[key] = read.value(ammo, key);
    else if (!['size', 'weight', 'cost_gp', 'equipment_slot', 'weapon_ammo_classification', 'passive_ability',
      'enchantment_template_applied', 'unenchantable'].includes(key)) return 'weapon:ammo-effects';
  }
  if (Object.keys(layers).length) {
    const reason = inspect({ items: { item_id: itemId }, item_layer_types: layers });
    if (reason) return reason;
  }
  const baseEffect = effects.get(itemId);
  return [...(baseEffect ? [baseEffect] : []), ...[...effects].filter(([id]) => id !== itemId).map(([, effect]) => effect)];
}

/** Materialize ammo through the weapon's own modifier/material lane and expose its
 * damage grants to the same passive executor. Neither held inventory row changes. */
function ammoWeapon(weapon: Row, effects: readonly Row[], read: ExplicitWeaponReaders): Row {
  const result = weaponEnchantmentAttackLayers(weapon, read, effects);
  if ('reason' in result) throw new Error(result.reason);
  if (!effects.length) return weapon;
  const computed = record(weapon.computed), enchantment = record(computed?.enchantment);
  const abilities = record(enchantment?.abilities);
  return { ...result.weapon, computed: { ...computed, enchantment: { ...enchantment, abilities: { ...abilities,
    passive_ability: [...(Array.isArray(abilities?.passive_ability) ? abilities.passive_ability : []),
      ...effects.map(item_arr => ({ item_arr, built_from: 'ammunition' }))] } } } };
}

/** Shared inventory move stack decision for one projectile's destination. */
function projectileRecovery(source: Row, target: Row, encounter: Row, encounterId: string, targetId: string,
  read: ExplicitWeaponReaders, position: (row: Row, key: string) => unknown, uuid: () => string,
  statusIds: (row: Row) => string[], floor: boolean): NonNullable<WeaponInventoryChange['recovery']> | { reason: string } {
  const objectId = upper(read.inventoryField(target, 'inventory_id'));
  if (floor) return floorProjectileRecovery(source, encounter, encounterId, {
    x: Number(position(target, 'x_pos') ?? 0), y: Number(position(target, 'y_pos') ?? 0), z: Number(position(target, 'z_pos') ?? 0),
  }, objectId ? { ...read, entries: (row, key) => key === 'encounter_inventories_arr'
    ? read.entries(row, key).filter(entry => upper(read.inventoryField(entry as Row, 'inventory_id')) !== objectId)
    : read.entries(row, key) } : read, position, uuid, statusIds);
  return projectileDestination(source, read.entries(target, 'inventory') as Row[], targetId, { x: 0, y: 0, z: 0 },
    read, position, uuid, statusIds, false);
}

/** One unit lands at a point, using the same split / merge decision as ammunition recovery. */
export function floorProjectileRecovery(source: Row, encounter: Row, encounterId: string, point: { x: number; y: number; z: number },
  read: ExplicitWeaponReaders, position: (row: Row, key: string) => unknown, uuid: () => string,
  statusIds: (row: Row) => string[]): NonNullable<WeaponInventoryChange['recovery']> | { reason: string } {
  return projectileDestination(source, read.entries(encounter, 'encounter_inventories_arr') as Row[], encounterId, point,
    read, position, uuid, statusIds, true);
}

function projectileDestination(source: Row, rows: Row[], holder: string, { x, y, z }: { x: number; y: number; z: number },
  read: ExplicitWeaponReaders, position: (row: Row, key: string) => unknown, uuid: () => string,
  statusIds: (row: Row) => string[], floor: boolean): NonNullable<WeaponInventoryChange['recovery']> | { reason: string } {
  if (![x, y, z].every(Number.isFinite)) return { reason: 'weapon:projectile-position-unavailable' };
  const stack = stackTarget(source, rows as Row[], { isFloor: floor, x, y, z }, 0, { value: read.value, itemType: read.itemType,
    inventoryField: (row, key) => row && ['x_pos', 'y_pos', 'z_pos'].includes(key) ? position(row, key) ?? 0 : read.inventoryField(row ?? null, key), statusItemIds: statusIds });
  if ('unknown' in stack) return { reason: 'weapon:projectile-' + stack.unknown };
  const existing = stack.target ? rows.find(r => upper(read.inventoryField(r as Row, 'inventory_id')) === stack.target) as Row : null;
  const beforeQty = existing ? Number(read.inventoryField(existing, 'item_qty') ?? 1) : 0;
  if (!Number.isInteger(beforeQty) || beforeQty < 0) return { reason: 'weapon:projectile-quantity-unavailable' };
  const qty = Number(read.inventoryField(source, 'item_qty') ?? 1);
  const recoveryId = stack.target ?? (qty === 1 ? upper(read.inventoryField(source, 'inventory_id')) : uuid());
  const row: Row = { inventory: { ...(source.inventory as Row), inventory_id: recoveryId, possess_id: holder, equipped: '',
    item_qty: beforeQty + 1, inventory_deleted: 0, inventory_wares: 0 }, position: { x_pos: x, y_pos: y, z_pos: z } };
  // Carry this projectile's effect metadata, never its possessor's catalog or stack totals.
  for (const key of ['items', 'item_layer_types', 'enchantment_templates', 'enchantment_template_enchantments',
    'passive_ability', 'enchantment_template_applied']) if (key in source) row[key] = source[key];
  const enchantment = record(record(source.computed)?.enchantment);
  if (enchantment) row.computed = { enchantment };
  const before = existing ? { possess_id: String(read.inventoryField(existing, 'possess_id') ?? ''),
    inventory_deleted: Number(read.inventoryField(existing, 'inventory_deleted') ?? 0), equipped: String(read.inventoryField(existing, 'equipped') ?? ''),
    x_pos: Number(position(existing, 'x_pos') ?? 0), y_pos: Number(position(existing, 'y_pos') ?? 0), z_pos: Number(position(existing, 'z_pos') ?? 0) } : undefined;
  return { inventory_id: recoveryId, possess_id: holder, created: !stack.target && qty > 1, before_qty: beforeQty, before,
    item_qty: beforeQty + 1, x_pos: x, y_pos: y, z_pos: z, row };
}

/** Native first matching positive stack, including bags. The page reader supplies
 * classification and lean quantity; inventory order is preserved. */
export function findWeaponAmmo(caster: Row, weapon: Row, read: ExplicitWeaponReaders): Row | null {
  const classification = read.value(weapon, 'uses_ammo_classification');
  const walk = (rows: unknown[]): Row | null => {
    for (const raw of rows) {
      if (!raw || typeof raw !== 'object') continue;
      const row = raw as Row;
      if (read.itemType(row) === 'ammo' && read.value(row, 'weapon_ammo_classification') === classification
          && Number(read.inventoryField(row, 'item_qty') ?? 1) > 0) return row;
      if (read.itemType(row) === 'container' && Array.isArray(row.inventory_in_container)) {
        const found = walk(row.inventory_in_container); if (found) return found;
      }
    }
    return null;
  };
  return walk(read.entries(caster, 'inventory'));
}

/** Precompute BOTH possible recovery destinations before any dice. The random
 * recovery choice remains in the browser, after the swing, as in the native lane. */
export function planWeaponAmmo(ammo: Row, target: Row, encounter: Row, encounterId: string, targetId: string,
  read: ExplicitWeaponReaders, position: (row: Row, key: string) => unknown, uuid: () => string, statusIds: (row: Row) => string[]):
  { weapon: (weapon: Row) => Row; finish: (hit: boolean, roll: RollSource) => WeaponInventoryChange } | { reason: string } {
  const effects = statusIds(ammo).length ? 'weapon:ammo-effects' : ammoEffectPlan(ammo, read);
  if (typeof effects === 'string') return { reason: effects };
  const qty = Number(read.inventoryField(ammo, 'item_qty') ?? 1);
  if (!Number.isInteger(qty) || qty < 1) return { reason: 'weapon:ammo-quantity-unavailable' };
  const before = { possess_id: String(read.inventoryField(ammo, 'possess_id') ?? ''), item_qty: qty,
    inventory_deleted: Number(read.inventoryField(ammo, 'inventory_deleted') ?? 0) };
  const id = upper(read.inventoryField(ammo, 'inventory_id'));
  const sustainable = SUSTAINABLE_AMMO.has(upper(read.value(ammo, 'weapon_ammo_classification')));
  const objectTarget = !!read.inventoryField(target, 'inventory_id');
  const recoveries: WeaponInventoryChange['recovery'][] = [];
  if (sustainable) for (const floor of objectTarget ? [true] : [true, false]) {
    const recovery = projectileRecovery(ammo, target, encounter, encounterId, targetId, read, position, uuid, statusIds, floor);
    if ('reason' in recovery) return recovery;
    recoveries.push(recovery);
  }
  return { weapon: weapon => ammoWeapon(weapon, effects, read), finish(hit, roll) {
    // rand(0,1): sustainable ammo survives on 0; a hit then rolls where it lands.
    const survives = roll.d(2) === 1 && sustainable;
    const recovery = survives ? recoveries[!objectTarget && hit && roll.d(2) === 2 ? 1 : 0] : undefined;
    return { inventory_id: id, before, after: { item_qty: qty - 1, inventory_deleted: qty === 1 ? 1 : 0 }, recovery,
      log_type: recovery ? recovery.possess_id === encounterId ? 'drop_inventory' : 'impale_inventory' : 'inventory_deleted' };
  } };
}

/** Consumption uses the same move receipt projection as an inventory split or
 * merge; a destroyed destination is removed by that lane's existing settlement. */
export function weaponInventoryChangeReceipt(change: WeaponInventoryChange, actorId: string, encounterId: string, operationId: string): InventoryMoveReceipt {
  const r = change.recovery;
  return { schema: 'ft3d.inventory-move-receipt.v1', ok: true, mutation_id: operationId,
    move: { outcome: r?.created ? 'partial_split' : r && r.inventory_id !== change.inventory_id ? (change.after.item_qty ? 'partial_merge' : 'full_merge') : 'full_move',
      moved_qty: 1, source: { inventory_id: change.inventory_id, possess_id_before: change.before.possess_id,
        item_qty_after: change.after.item_qty, deleted_after: !!change.after.inventory_deleted },
      destination: { inventory_id: r?.inventory_id ?? operationId, possess_id: r?.possess_id ?? change.before.possess_id,
        item_qty_after: r?.item_qty ?? change.after.item_qty, created: r?.created ?? false, deleted_after: !r,
        equipped: '', position: { x: r?.x_pos ?? 0, y: r?.y_pos ?? 0, z: r?.z_pos ?? 0 }, ...(r ? { row: r.row } : {}) },
      roots: { source: change.before.possess_id, destination: r?.possess_id ?? null } },
    affected_caster_ids: [actorId], affected_encounter_ids: [encounterId], timing: { accepted_unix_ms: 0, commit_ms: 0 } };
}

/** Late acknowledgments cannot move a weapon that has since changed holders. */
export function canProjectThrownWeapon(move: WeaponInventoryMove, actor: Row, read: ExplicitWeaponReaders): boolean {
  const row = read.entries(actor, 'inventory').find(entry => upper(read.inventoryField(entry as Row, 'inventory_id')) === move.inventory_id) as Row | undefined;
  return !!row && upper(read.inventoryField(row, 'possess_id')) === upper(move.before.possess_id)
    && String(read.inventoryField(row, 'equipped') ?? '') === move.before.equipped;
}

/** Both whole-row and split throws share stack decisions and the native hit-only impale draw. */
export function planThrownWeapon(weapon: Row, target: Row, encounter: Row, actorId: string, encounterId: string,
  read: ExplicitWeaponReaders, position: (row: Row, key: string) => unknown, statusIds: (row: Row) => string[], uuid: () => string):
  { finish: (hit: boolean, roll: RollSource) => WeaponInventoryChange } | { reason: string } {
  const qty = Number(read.inventoryField(weapon, 'item_qty') ?? 1);
  if (upper(read.inventoryField(weapon, 'possess_id')) !== upper(actorId)) return { reason: 'weapon:throw-holder-unavailable' };
  if (!Number.isInteger(qty) || qty < 1) return { reason: 'weapon:throw-quantity-unavailable' };
  if (statusIds(weapon).length) return { reason: 'weapon:throw-stack-statuses' };
  const targetId = String(read.creatureField?.(target, 'encounter_creature_id') ?? '');
  const objectTarget = !!read.inventoryField(target, 'inventory_id');
  const recoveries: NonNullable<WeaponInventoryChange['recovery']>[] = [];
  for (const floor of objectTarget ? [true] : [true, false]) {
    const recovery = projectileRecovery(weapon, target, encounter, encounterId, targetId, read, position, uuid, statusIds, floor);
    if ('reason' in recovery) return recovery;
    recoveries.push(recovery);
  }
  return { finish(hit, roll) {
    const recovery = recoveries[!objectTarget && hit && roll.d(2) === 2 ? 1 : 0]!;
    return { inventory_id: upper(read.inventoryField(weapon, 'inventory_id')), before: {
      possess_id: String(read.inventoryField(weapon, 'possess_id') ?? ''), item_qty: qty,
      inventory_deleted: Number(read.inventoryField(weapon, 'inventory_deleted') ?? 0) },
      after: { item_qty: qty - 1, inventory_deleted: qty === 1 ? 1 : 0 }, recovery,
      log_type: recovery.possess_id === encounterId ? 'drop_inventory' : 'impale_inventory' };
  } };
}

export function thrownWeaponReceipt(move: WeaponInventoryMove, operationId: string): InventoryMoveReceipt {
  return { schema: 'ft3d.inventory-move-receipt.v1', ok: true, mutation_id: operationId,
    move: { outcome: 'full_move', moved_qty: 1,
      source: { inventory_id: move.inventory_id, possess_id_before: move.before.possess_id, item_qty_after: 1, deleted_after: false },
      destination: { inventory_id: move.inventory_id, possess_id: move.after.possess_id, item_qty_after: 1, created: false, equipped: '',
        position: { x: move.after.x_pos, y: move.after.y_pos, z: move.after.z_pos } },
      roots: { source: move.before.possess_id, destination: move.after.possess_id } },
    affected_caster_ids: [move.before.possess_id], affected_encounter_ids: [move.after.possess_id],
    timing: { accepted_unix_ms: 0, commit_ms: 0 } };
}
